/* =============================================================================
   Locale-aware value formatting and parsing
   -----------------------------------------------------------------------------
   The contract: presentation is always separate from the typed value.

     - money      → value is a `number`, displayed as the locale's currency
     - percentage → value is a `number`, displayed as `<number>%` with the sign
                    AFTER the number (the common internet pattern)
     - date/time  → value is an ISO-8601 string, displayed in the locale's order

   By default everything follows the interface locale when there is one — the
   language the reader chose in the selector — and the browser/OS locale when
   the app is running without a `LocaleProvider`, which is the state the
   showcase and the Storybook locale toggle are in. A caller may always pin a
   specific input with `forceLocale`, which is how those two exercise both
   orders side by side.
   ============================================================================= */

import { activeLocale } from './locale';

export type LocaleInput = string | undefined | null;

/**
 * Shorthand aliases accepted by `forceLocale`, mapped to real BCP-47 tags.
 * `'us'` is the documented shorthand for US formatting (MM/DD/YYYY, $ 2,000.00).
 */
const LOCALE_ALIASES: Record<string, string> = {
  us: 'en-US',
  usa: 'en-US',
  'en-us': 'en-US',
  br: 'pt-BR',
  brazil: 'pt-BR',
  'pt-br': 'pt-BR',
  uk: 'en-GB',
  gb: 'en-GB',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  jp: 'ja-JP',
};

/** The currency assumed for a locale when the caller does not name one. */
const LOCALE_CURRENCY: Record<string, string> = {
  'en-US': 'USD',
  'en-GB': 'GBP',
  'pt-BR': 'BRL',
  'de-DE': 'EUR',
  'fr-FR': 'EUR',
  'es-ES': 'EUR',
  'ja-JP': 'JPY',
};

function browserLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return 'en-US';
}

/**
 * Resolves the effective locale: an explicit override wins, then the interface
 * locale, then the browser/OS one.
 *
 * The middle rung is what makes a chosen language mean something beyond words.
 * A reader who picks Portuguese from a browser reporting `en-US` is asking for
 * `R$ 2.000,00` and `22/11/2026` as well as for Portuguese, and a value that
 * kept formatting as `$ 2,000.00` under a Portuguese page reads as a bug.
 */
export function resolveLocale(forceLocale?: LocaleInput): string {
  if (!forceLocale) return activeLocale() ?? browserLocale();
  const key = String(forceLocale).trim();
  return LOCALE_ALIASES[key.toLowerCase()] ?? key;
}

/** A locale-safe cache key — `Intl` constructors are expensive to re-create. */
const formatterCache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

/**
 * Numerals and calendar are pinned for every formatter.
 *
 * Without this, presentation and parsing disagree for whole classes of locale,
 * and a value is shown in a form that cannot be typed back in:
 *
 *   ar-EG  formatDate('2026-05-03') -> '٠٣‏/٠٥‏/٢٠٢٦'   Arabic-Indic digits
 *   fa-IR  formatDate('2026-05-03') -> '1405/02/13'      Persian calendar
 *   th-TH  formatDate('2026-05-03') -> '03/05/2569'      Buddhist calendar
 *
 * The typed value here is always an ISO-8601 Gregorian string, and the native
 * date input, the keyboard and the parsers in this module all work in Latin
 * digits. Pinning keeps the three in the same alphabet and the same calendar,
 * so what a field shows is what the field accepts.
 *
 * Only these two are pinned — everything else about the locale (order of
 * parts, separators, month names, currency placement) still follows it. A
 * caller that explicitly passes `numberingSystem` or `calendar` still wins.
 */
const PINNED_LOCALE_OPTIONS = { numberingSystem: 'latn', calendar: 'gregory' } as const;

/**
 * Invisible bidirectional control characters that `Intl` inserts around
 * currency symbols and date parts in RTL locales (U+200E, U+200F, U+061C,
 * U+2066-U+2069). They are meaningful for rendering and meaningless for
 * parsing, so they are stripped before any value is read back.
 */
const BIDI_CONTROLS = /[‎‏؜⁦-⁩]/g;

/** Removes bidi controls so a displayed string can be parsed again. */
export function stripBidiControls(value: string): string {
  return value.replace(BIDI_CONTROLS, '');
}

function cachedNumberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const merged = { ...PINNED_LOCALE_OPTIONS, ...options };
  const key = `n:${locale}:${JSON.stringify(merged)}`;
  const hit = formatterCache.get(key);
  if (hit instanceof Intl.NumberFormat) return hit;
  const created = new Intl.NumberFormat(locale, merged);
  formatterCache.set(key, created);
  return created;
}

function cachedDateFormat(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const merged = { ...PINNED_LOCALE_OPTIONS, ...options };
  const key = `d:${locale}:${JSON.stringify(merged)}`;
  const hit = formatterCache.get(key);
  if (hit instanceof Intl.DateTimeFormat) return hit;
  const created = new Intl.DateTimeFormat(locale, merged);
  formatterCache.set(key, created);
  return created;
}

/* -----------------------------------------------------------------------------
   Symbols and separators
   -------------------------------------------------------------------------- */

export function currencyFor(locale: string, override?: string): string {
  if (override) return override;
  return LOCALE_CURRENCY[locale] ?? LOCALE_CURRENCY[locale.split('-')[0] ?? ''] ?? 'USD';
}

/**
 * The locale's decimal and group separators, read from `Intl` rather than
 * assumed, so parsing always agrees with formatting.
 */
function separators(locale: string): { decimal: string; group: string } {
  const parts = cachedNumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    useGrouping: true,
  }).formatToParts(12345.6);

  return {
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
    group: parts.find((p) => p.type === 'group')?.value ?? ',',
  };
}

/** The order a locale writes a numeric date in. */
export function dateOrder(locale: string): 'mdy' | 'dmy' | 'ymd' {
  const parts = cachedDateFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(Date.UTC(2026, 10, 22)));

  const order = parts
    .filter((p) => p.type === 'year' || p.type === 'month' || p.type === 'day')
    .map((p) => p.type.charAt(0))
    .join('');

  if (order === 'dmy' || order === 'ymd') return order;
  return 'mdy';
}

/* -----------------------------------------------------------------------------
   Formatting
   -------------------------------------------------------------------------- */

/**
 * Money is rendered with a space between the currency symbol and the number —
 * `$ 2,000.00`, `R$ 2.000,00` — which is the format the design system commits
 * to. `Intl` omits that space for many locales, so the parts are reassembled.
 */
export function formatMoney(
  value: number | null | undefined,
  options: { locale?: LocaleInput; precision?: number; currency?: string } = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  const locale = resolveLocale(options.locale);
  const precision = options.precision ?? 2;
  const currency = currencyFor(locale, options.currency);

  const parts = cachedNumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    currencyDisplay: 'narrowSymbol',
  }).formatToParts(value);

  let out = '';
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (!part) continue;
    out += part.value;
    const next = parts[i + 1];
    // Insert one plain space after the currency symbol, and collapse the
    // non-breaking space Intl already emits so the result is predictable.
    if (part.type === 'currency' && next && next.type !== 'literal') out += ' ';
  }
  return out.replace(/ /g, ' ').trim();
}

/** Percentage with the sign AFTER the number: `35.55%` / `35,55%`. */
export function formatPercentage(
  value: number | null | undefined,
  options: { locale?: LocaleInput; precision?: number } = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  const locale = resolveLocale(options.locale);
  const precision = options.precision ?? 2;
  const number = cachedNumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: false,
  }).format(value);
  return `${number}%`;
}

export function formatNumber(
  value: number | null | undefined,
  options: { locale?: LocaleInput; precision?: number; grouping?: boolean } = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  const locale = resolveLocale(options.locale);
  const precision = options.precision ?? 0;
  return cachedNumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: options.grouping ?? true,
  }).format(value);
}

/**
 * Parses an ISO-8601 string or `Date` without throwing on bad input.
 *
 * The subtlety that matters here: ECMA-262 parses a **date-only** string
 * (`2026-01-05`) as UTC midnight, while a date-time string (`2026-01-05T00:00`)
 * is parsed as local time. So the first form renders as the *previous day* for
 * anyone west of UTC — `formatDate('2026-01-05')` showed `01/04/2026` in
 * America/New_York. Date-only and month-only values are therefore parsed
 * explicitly as local time, which is what a calendar date means to a reader.
 */
function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  const text = value.trim();

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (dateOnly) {
    const [, y, m, d] = dateOnly as unknown as [string, string, string, string];
    const local = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(local.getTime()) ? null : local;
  }

  const monthOnly = /^(\d{4})-(\d{2})$/.exec(text);
  if (monthOnly) {
    const [, y, m] = monthOnly as unknown as [string, string, string];
    const local = new Date(Number(y), Number(m) - 1, 1);
    return Number.isNaN(local.getTime()) ? null : local;
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDate(
  value: string | number | Date | null | undefined,
  options: { locale?: LocaleInput; style?: 'short' | 'medium' | 'long' | 'monthYear' } = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  const locale = resolveLocale(options.locale);
  const style = options.style ?? 'short';

  if (style === 'monthYear') {
    return cachedDateFormat(locale, { year: 'numeric', month: 'short' }).format(date);
  }
  if (style === 'medium') {
    return cachedDateFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
  }
  if (style === 'long') {
    return cachedDateFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  }
  return cachedDateFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  options: { locale?: LocaleInput; withSeconds?: boolean } = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  const locale = resolveLocale(options.locale);
  return cachedDateFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(options.withSeconds ? { second: '2-digit' as const } : {}),
  }).format(date);
}

/** The placeholder that teaches the user the expected date order. */
export function datePlaceholder(locale: string): string {
  switch (dateOrder(locale)) {
    case 'dmy':
      return 'DD/MM/YYYY';
    case 'ymd':
      return 'YYYY-MM-DD';
    default:
      return 'MM/DD/YYYY';
  }
}

/* -----------------------------------------------------------------------------
   Parsing
   -------------------------------------------------------------------------- */

/**
 * Parses a money string back to a number. Accepts the locale's own separators,
 * a stray currency symbol, spaces and a leading sign. Returns `null` when the
 * text cannot be read as a number at all.
 */
export function parseMoney(
  input: string,
  options: { locale?: LocaleInput } = {},
): number | null {
  const locale = resolveLocale(options.locale);
  return parseLocalizedNumber(input, locale);
}

export function parsePercentage(
  input: string,
  options: { locale?: LocaleInput } = {},
): number | null {
  const locale = resolveLocale(options.locale);
  return parseLocalizedNumber(input.replace(/%/g, ''), locale);
}

export function parseNumber(
  input: string,
  options: { locale?: LocaleInput } = {},
): number | null {
  const locale = resolveLocale(options.locale);
  return parseLocalizedNumber(input, locale);
}

function parseLocalizedNumber(input: string, locale: string): number | null {
  if (!input) return null;
  const { decimal, group } = separators(locale);

  // Bidi controls are invisible in the field but present in the string — an RTL
  // locale wraps the currency symbol and the separators in them — so they are
  // stripped before the character-class filter below runs.
  // Keep digits, the minus sign, and whichever separators the locale uses.
  let cleaned = stripBidiControls(input).replace(/\s/g, '').replace(/ /g, '');
  cleaned = cleaned.replace(new RegExp(`[^0-9\\-${escapeRegExp(decimal)}${escapeRegExp(group)}]`, 'g'), '');

  // Normalise to a plain `-1234.56` shape before handing it to Number.
  if (group !== decimal) cleaned = cleaned.split(group).join('');
  if (decimal !== '.') cleaned = cleaned.split(decimal).join('.');

  // Reject anything with an interior minus or more than one minus.
  if ((cleaned.match(/-/g) ?? []).length > 1) return null;
  if (cleaned.indexOf('-') > 0) return null;

  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Parses a date typed in the locale's order. Returns an ISO-8601 string (the
 * value type the contract commits to) or `null` when the text is not a real
 * calendar date — `02/31/2026` is rejected rather than rolled over.
 */
export function parseDateInput(input: string, locale: string): string | null {
  // Strip bidi controls first: an RTL locale renders the same digits with
  // invisible direction marks around the separators, which would otherwise fail
  // the anchored match below and reject a perfectly valid date.
  const match = stripBidiControls(input)
    .trim()
    .match(/^(\d{1,4})[/\-.](\d{1,2})[/\-.](\d{1,4})$/);
  if (!match) return null;

  const [, a, b, c] = match as unknown as [string, string, string, string];
  let year: number;
  let month: number;
  let day: number;

  switch (dateOrder(locale)) {
    case 'dmy':
      day = Number(a);
      month = Number(b);
      year = Number(c);
      break;
    case 'ymd':
      year = Number(a);
      month = Number(b);
      day = Number(c);
      break;
    default:
      month = Number(a);
      day = Number(b);
      year = Number(c);
  }

  if (year < 100) year += 2000;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const date = new Date(Date.UTC(year, month - 1, day));
  // Reject a rolled-over date such as 31 February.
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Parses `HH:MM` (24-hour) into a `HH:MM:00` fragment, or `null`. */
export function parseTimeInput(input: string): string | null {
  const match = input.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
}
